// Servicio de autenticación para Olimpo Web
import { API_BASE_URL } from '../config/api';

class AuthService {
  constructor() {
    this.apiBase = API_BASE_URL;
    this.token = localStorage.getItem('auth_token');
  }

  // 1. Obtener URL de autorización de Google
  async getGoogleAuthURL() {
    try {
      const response = await fetch(`${this.apiBase}/api/auth/google`);
      if (!response.ok) {
        throw new Error('Error obteniendo URL de autorización');
      }
      return await response.json();
    } catch (error) {
      console.error('Error en getGoogleAuthURL:', error);
      throw error;
    }
  }

  // 2. Iniciar proceso de login con Google
  async startGoogleLogin() {
    try {
      const { auth_url, state } = await this.getGoogleAuthURL();
      
      // Debug logs
      console.log('Starting Google Login:', {
        auth_url: auth_url ? `${auth_url.substring(0, 50)}...` : null,
        state,
        currentUrl: window.location.href,
        hostname: window.location.hostname,
        environment: process.env.NODE_ENV,
        apiBase: this.apiBase
      });
      
      // Guardar state para validación posterior
      sessionStorage.setItem('oauth_state', state);
      
      // Redirigir a Google
      window.location.href = auth_url;
    } catch (error) {
      console.error('Error iniciando login:', error);
      throw error;
    }
  }

  // 3. Manejar callback de Google
  async handleGoogleCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const storedState = sessionStorage.getItem('oauth_state');

    // Debug logs
    console.log('Google Callback Debug:', {
      code: code ? `${code.substring(0, 20)}...` : null,
      state,
      storedState,
      currentUrl: window.location.href,
      hostname: window.location.hostname,
      environment: process.env.NODE_ENV
    });

    if (!code) {
      throw new Error('No se recibió código de autorización');
    }

    if (state !== storedState) {
      console.error('State mismatch:', {
        received: state,
        stored: storedState,
        match: state === storedState
      });
      throw new Error('Estado de seguridad no coincide');
    }

    try {
      const response = await fetch(`${this.apiBase}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, state })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error en el login');
      }

      const { token, user, expires_at } = await response.json();
      
      // Guardar token y limpiar state
      this.token = token;
      localStorage.setItem('auth_token', token);
      sessionStorage.removeItem('oauth_state');
      
      return { user, expires_at };
    } catch (error) {
      console.error('Error en callback:', error);
      throw error;
    }
  }

  // 4. Registrar nuevo usuario
  async register(code, state, role = null, siteId = null) {
    try {
      const response = await fetch(`${this.apiBase}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          code, 
          state, 
          role, 
          site_id: siteId 
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error en el registro');
      }

      const { token, user, expires_at } = await response.json();
      
      this.token = token;
      localStorage.setItem('auth_token', token);
      
      return { user, expires_at };
    } catch (error) {
      console.error('Error en registro:', error);
      throw error;
    }
  }

  // 5. Obtener perfil del usuario
  async getProfile() {
    if (!this.token) {
      throw new Error('No hay token de autenticación');
    }

    try {
      const response = await fetch(`${this.apiBase}/api/profile`, {
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expirado, intentar renovar
          await this.refreshToken();
          return this.getProfile();
        }
        throw new Error('Error obteniendo perfil');
      }

      return await response.json();
    } catch (error) {
      console.error('Error obteniendo perfil:', error);
      throw error;
    }
  }

  // 6. Renovar token
  async refreshToken() {
    if (!this.token) {
      throw new Error('No hay token para renovar');
    }

    try {
      const response = await fetch(`${this.apiBase}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error renovando token');
      }

      const { token, expires_at } = await response.json();
      
      this.token = token;
      localStorage.setItem('auth_token', token);
      
      return { token, expires_at };
    } catch (error) {
      console.error('Error renovando token:', error);
      // Si no se puede renovar, limpiar token
      this.logout();
      throw error;
    }
  }

  // 7. Hacer request autenticado
  async authenticatedRequest(url, options = {}) {
    if (!this.token) {
      throw new Error('No hay token de autenticación');
    }

    const defaultOptions = {
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    };

    try {
      const response = await fetch(url, { ...defaultOptions, ...options });
      
      if (response.status === 401) {
        // Token expirado, intentar renovar
        await this.refreshToken();
        // Reintentar con nuevo token
        return fetch(url, { 
          ...defaultOptions, 
          ...options,
          headers: {
            ...defaultOptions.headers,
            'Authorization': `Bearer ${this.token}`
          }
        });
      }

      return response;
    } catch (error) {
      console.error('Error en request autenticado:', error);
      throw error;
    }
  }

  // 8. Logout
  async logout() {
    try {
      if (this.token) {
        await fetch(`${this.apiBase}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.token}`
          }
        });
      }
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      this.token = null;
      localStorage.removeItem('auth_token');
      sessionStorage.removeItem('oauth_state');
    }
  }

  // 9. Verificar si está autenticado
  isAuthenticated() {
    // Siempre verificar el localStorage para asegurar que el token esté actualizado
    this.token = localStorage.getItem('auth_token');
    return !!this.token;
  }

  // 10. Obtener token actual
  getToken() {
    // Siempre obtener el token más reciente del localStorage
    this.token = localStorage.getItem('auth_token');
    return this.token;
  }

  // 11. Verificar si el usuario tiene un rol específico
  hasRole(role) {
    // Esta función requerirá que el perfil del usuario esté cargado
    // Se implementará en el contexto de autenticación
    return false;
  }

  // 12. Verificar si el usuario tiene acceso a un site
  hasSiteAccess(siteId) {
    // Esta función requerirá que el perfil del usuario esté cargado
    // Se implementará en el contexto de autenticación
    return false;
  }
}

// Crear instancia singleton
const authService = new AuthService();

export default authService;
